import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { getManagementPendingOrders, getManagementOrderById, updateManagementOrderStatus, uploadOrderAttachments, getOrderAttachments } from '../services/api';
import { refreshAccessToken } from '../services/auth';
import { formatDateTime, getStatusColor, handleApiError, calculateOrderTotal, getDiscountDetails } from '../utils/helpers';
import OrderDetailsView from './OrderDetailsView';
import DateRangePicker from '../components/common/DateRangePicker';
import SuccessModal from '../components/common/SuccessModal';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const SupplierPendingOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]); // Store original pending orders
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const { accessToken } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState('');
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');

    // Success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({ orderId: '', action: '', status: '' });

    // Attachments state (mirroring management view)
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState('');
    const [attachmentSuccess, setAttachmentSuccess] = useState('');
    const [processingFiles, setProcessingFiles] = useState(false);
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [existingAttachmentsLoading, setExistingAttachmentsLoading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadingAttachments, setUploadingAttachments] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);
    const fileInputRef = useRef(null);

    const formatFileSize = (bytes) => {
        if (!bytes && bytes !== 0) return '—';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const autoRetryRef = useRef(false);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Use management API to get pending orders with pagination
            const result = await getManagementPendingOrders({ page: 0, size: 50, status: 'Pending' });

            // Handle paginated response
            let ordersArray;
            if (result.content && result.totalElements) {
                // Paginated response
                ordersArray = result.content;
            } else {
                // Simple array response
                ordersArray = result;
            }

            // Ensure we only work with pending orders
            const pendingOrders = ordersArray.filter(order => order.status === 'PENDING');
            setOrders(pendingOrders);

            // Fetch details for each order to get totalAmount
            const detailsPromises = pendingOrders.map(async (order) => {
                try {
                    const details = await getManagementOrderById(order.id, 'Pending');
                    return { ...order, totalAmount: details.totalAmount, totalAmountToBePaid: details.totalAmountToBePaid, discountDetails: details.discountDetails };
                } catch (e) {
                    return order; // fallback if details fail
                }
            });
            let ordersWithTotals = await Promise.all(detailsPromises);
            // Sort by createdAt (newest first)
            ordersWithTotals = ordersWithTotals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setOrders(ordersWithTotals);
            setOriginalOrders(ordersWithTotals); // Store original orders
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            if (
                !autoRetryRef.current &&
                err &&
                typeof err.message === "string" &&
                err.message.includes("Request failed after repeated attempts")
            ) {
                autoRetryRef.current = true;
                setTimeout(() => {
                    fetchOrders();
                }, 1000);
                return;
            }
            setError('Failed to fetch pending orders');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = async (orderId) => {
        setDetailsError(null);
        setDetailsLoading(true);
        try {
            const details = await getManagementOrderById(orderId, 'Pending');
            setSelectedOrder(details);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setDetailsError('Failed to fetch order details');
            setSelectedOrder(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessModalData({ orderId: '', action: '', status: '' });
    };

    const handleOrderUpdate = (orderId, newStatus, successData = null) => {
        // Remove the updated order from the orders list
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
        setOriginalOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));

        // Close the details view
        setSelectedOrder(null);
        setDetailsError(null);

        // Show success modal if success data is provided
        if (successData) {
            setSuccessModalData(successData);
            setShowSuccessModal(true);
        }
    };

    const handleAction = async (action) => {
        if (!selectedOrder || !selectedOrder.id) return;

        setActionLoading(true);
        setActionError('');

        try {
            let status;
            if (action === 'reject') {
                status = 'REJECTED';
            } else if (action === 'finalize') {
                // For supplier view, finalize means completing the order
                status = 'COMPLETED';
            } else {
                setActionError('Invalid action');
                return;
            }

            await updateManagementOrderStatus(selectedOrder.id, status);

            // Call the callback to update the component with success data
            handleOrderUpdate(selectedOrder.id, status, {
                orderId: selectedOrder.id,
                action: action,
                status: status
            });

        } catch (error) {
            console.error('Action failed:', error);

            // Handle specific error cases
            if (error.message.includes('Access denied') || error.message.includes('403')) {
                setActionError(`Access denied. You may not have permission to ${action} orders. Please contact your administrator.`);
            } else {
                setActionError(`Failed to ${action} order: ${error.message}`);
            }
        } finally {
            setActionLoading(false);
        }
    };

    // Attachments: file add with validation (same as management)
    const handleFilesAdd = (incomingFiles) => {
        setAttachmentError('');
        setAttachmentSuccess('');
        setProcessingFiles(true);
        const files = Array.from(incomingFiles || []);
        const allowed = ['image/png', 'image/jpeg', 'application/pdf', 'application/zip', 'application/x-zip-compressed'];
        const maxSize = 20 * 1024 * 1024; // 20 MB

        const valid = files.filter((f) => allowed.includes(f.type) && f.size <= maxSize);

        const invalidTypeFiles = files.filter((f) => !allowed.includes(f.type));
        if (invalidTypeFiles.length > 0) {
            const fileNames = invalidTypeFiles.map(f => f.name).join(', ');
            setAttachmentError(`Unsupported file type(s): ${fileNames}. Only PNG, JPEG, PDF, and ZIP files are allowed.`);
        }

        const oversizedFiles = files.filter((f) => f.size > maxSize);
        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.map(f => f.name).join(', ');
            const sizes = oversizedFiles.map(f => (f.size / (1024 * 1024)).toFixed(2)).join(', ');
            setAttachmentError(`Files exceeding 20 MB limit: ${fileNames} (${sizes} MB). Please compress or resize these files.`);
        }

        if (valid.length > 0) {
            setAttachments((prev) => [...prev, ...valid]);
            if (valid.length === 1) {
                setAttachmentSuccess(`${valid[0].name} added successfully`);
            } else {
                setAttachmentSuccess(`${valid.length} files added successfully`);
            }
            setTimeout(() => setAttachmentSuccess(''), 3000);
        }
        setProcessingFiles(false);
    };

    const handleViewFile = (file) => {
        try {
            const url = URL.createObjectURL(file);
            window.open(url, '_blank', 'noopener,noreferrer');
            setTimeout(() => URL.revokeObjectURL(url), 60 * 1000);
        } catch (e) {
            console.error('Failed to open file preview:', e);
            setAttachmentError('Could not open file preview.');
        }
    };

    const handleUploadAttachments = async () => {
        if (!selectedOrder?.id || attachments.length === 0) return;

        setUploadingAttachments(true);
        setAttachmentError('');
        setAttachmentSuccess('');
        setUploadSuccess(false);
        setUploadProgress(0);
        setCurrentFileIndex(0);
        setTotalFiles(attachments.length);

        try {
            const totalSteps = attachments.length * 10;
            let currentStep = 0;

            for (let i = 0; i < attachments.length; i++) {
                setCurrentFileIndex(i + 1);
                for (let step = 0; step < 10; step++) {
                    currentStep++;
                    const progress = (currentStep / totalSteps) * 30;
                    setUploadProgress(progress);
                    await new Promise(resolve => setTimeout(resolve, 30));
                }
                await uploadOrderAttachments(selectedOrder.id, [attachments[i]]);
            }

            setUploadProgress(100);
            await new Promise(resolve => setTimeout(resolve, 300));

            setUploadSuccess(true);
            setAttachments([]);
            fetchExistingAttachments();
            setTimeout(() => setUploadSuccess(false), 3000);
        } catch (error) {
            console.error('Failed to upload attachments:', error);
            setAttachmentError(`Upload failed: ${error.message}`);
        } finally {
            setUploadingAttachments(false);
            setUploadProgress(0);
            setCurrentFileIndex(0);
            setTotalFiles(0);
        }
    };

    const fetchExistingAttachments = async () => {
        if (!selectedOrder?.id) return;
        try {
            setExistingAttachmentsLoading(true);
            const data = await getOrderAttachments(selectedOrder.id);
            setExistingAttachments(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error('Failed to load attachments:', e);
        } finally {
            setExistingAttachmentsLoading(false);
        }
    };

    useEffect(() => {
        fetchExistingAttachments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrder?.id]);

    // Utility: check if query is likely an order ID (all digits or starts with 'ORD-')
    const isOrderId = (query) => /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

    // Synchronous input handler with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Debounce the search
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, 300); // 300ms delay
    };

    // Perform the actual search
    const performSearch = async (query) => {
        console.log('Performing search for:', query);
        console.log('Original orders count:', originalOrders.length);

        // Check if search query is empty or just whitespace
        if (!query || !query.trim()) {
            // No search query - restore original pending orders
            setOrders(originalOrders);
            setSearchLoading(false);
            return;
        }

        // Check if it's a numeric search (all digits or starts with 'ORD-')
        const isNumericSearch = /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);
        console.log('Is numeric search:', isNumericSearch);

        if (isNumericSearch) {
            // Use backend API for pending orders only
            setSearchLoading(true);
            setError(null);
            try {
                console.log('Calling getManagementOrderById with:', query);
                const order = await getManagementOrderById(query, 'Pending');
                console.log('Search result:', order);
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                if (order && order.id && order.status === 'PENDING') {
                    setOrders([order]);
                } else {
                    setOrders([]);
                }
            } catch (err) {
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                console.error('Search error:', err);
                setOrders([]);
            }
            setSearchLoading(false);
        } else {
            // Client-side filtering for non-numeric queries
            let filtered = originalOrders; // Use originalOrders instead of orders to ensure we only filter pending orders
            filtered = filtered.filter(order =>
                String(order.id).toLowerCase().includes(query.toLowerCase()) && order.status === 'PENDING'
            );
            console.log('Client-side filtered results:', filtered);
            setOrders(filtered);
            setSearchLoading(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Refetch on accessToken change or window focus
    useEffect(() => {
        fetchOrders();
        // Removed window focus refresh
    }, [accessToken]);

    // Don't return early for loading/error - show header and search bar always

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Success Modal */}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={handleCloseSuccessModal}
                orderId={successModalData.orderId}
                action={successModalData.action}
                status={successModalData.status}
            />

            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Pending Orders</h1>
                <p className="text-gray-600">Monitor pending orders that need your attention</p>
            </div>
            {!selectedOrder && (
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Search by Order ID..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {searchLoading && (
                        <div className="mt-2 text-sm text-gray-500">Searching...</div>
                    )}
                    <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <DateRangePicker
                            value={{ startDate: exportStartDate, endDate: exportEndDate }}
                            onApply={(s, e) => {
                                setExportStartDate(s);
                                setExportEndDate(e);
                            }}
                        />
                    </div>
                </div>
            )}
            {selectedOrder ? (
                <>
                    <button
                        className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                        onClick={handleBackToList}
                    >
                        ← Back to List
                    </button>
                    <OrderDetailsView order={selectedOrder}>
                        {/* Existing Attachments */}
                        <div className="mt-8">
                            <h3 className="text-lg font-semibold mb-2">Existing Attachments</h3>
                            {existingAttachmentsLoading ? (
                                <div className="text-sm text-gray-500">Loading attachments...</div>
                            ) : existingAttachments.length > 0 ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {existingAttachments.map((att) => (
                                        <div key={att.id} className="flex items-center justify-between border rounded p-2">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium">{att.name || att.filename || 'Attachment'}</span>
                                                <span className="text-xs text-gray-500">{formatFileSize(att.size || 0)}</span>
                                            </div>
                                            {att.link && (
                                                <a
                                                    href={`${process.env.REACT_APP_API_URL}${att.link?.replace('/api', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 text-sm hover:underline"
                                                >
                                                    View
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-sm text-gray-500">No attachments uploaded yet.</div>
                            )}
                        </div>

                        {/* Upload Attachments - only for PENDING */}
                        {selectedOrder.status === 'PENDING' && (
                            <div className="mt-6">
                                <h3 className="text-lg font-semibold mb-2">Upload Attachments</h3>
                                <div
                                    className={`border-2 border-dashed rounded p-6 text-center cursor-pointer ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsDragOver(true);
                                    }}
                                    onDragLeave={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsDragOver(false);
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsDragOver(false);
                                        handleFilesAdd(e.dataTransfer.files);
                                    }}
                                >
                                    <h3 className="text-lg font-semibold mb-3">Invoice Attachments</h3>
                                    <p className="text-sm text-gray-600 mb-3">Upload PNG, JPEG/JPG, PDF, or ZIP files related to this order. Maximum file size: 20 MB.</p>

                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => fileInputRef.current?.click()}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                fileInputRef.current?.click();
                                            }
                                        }}
                                        className={`w-full flex flex-col items-center justify-center gap-2 px-4 py-6 rounded-md border-2 border-dashed ${isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-white'} cursor-pointer`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center px-3 py-2 rounded-md bg-blue-100 text-blue-700 text-sm font-semibold hover:bg-blue-200">
                                                Choose files
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            Supported formats: PNG, JPEG, PDF, ZIP • Max size: 20 MB per file
                                        </div>
                                        <div className="text-xs text-blue-600 mt-1 font-medium">
                                            Click or drag files here to upload
                                        </div>
                                    </div>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,application/pdf,application/zip,application/x-zip-compressed,.zip"
                                        multiple
                                        className="hidden"
                                        title="Maximum file size: 20 MB per file. Supported formats: PNG, JPEG, PDF, ZIP"
                                        onChange={(e) => {
                                            handleFilesAdd(e.target.files);
                                            if (e.target) e.target.value = '';
                                        }}
                                    />

                                    {attachmentError && (
                                        <div className="mt-2 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
                                            <div className="flex items-start">
                                                <svg className="w-4 h-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                                </svg>
                                                <div>
                                                    <p className="font-medium">Upload Error</p>
                                                    <p className="text-red-600 mt-1">{attachmentError}</p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {attachmentSuccess && (
                                        <div className="mt-2 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md">
                                            <div className="flex items-center">
                                                <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                <span className="font-medium">{attachmentSuccess}</span>
                                            </div>
                                        </div>
                                    )}

                                    {attachments.length > 0 && (
                                        <div className="mt-3">
                                            <h4 className="text-sm font-semibold mb-2">Selected Files</h4>
                                            <ul className="divide-y divide-gray-200 bg-white border border-gray-200 rounded-md">
                                                {attachments.map((file, idx) => (
                                                    <li key={idx} className="flex items-center justify-between px-3 py-2">
                                                        <div className="text-sm text-gray-800 truncate mr-3">
                                                            {file.name}
                                                            <span className="ml-2 text-xs text-gray-500">({formatFileSize(file.size)})</span>
                                                        </div>
                                                        <div className="flex items-center gap-4">
                                                            <button
                                                                className="text-blue-600 hover:text-blue-700 text-sm"
                                                                onClick={() => handleViewFile(file)}
                                                            >
                                                                View
                                                            </button>
                                                            <button
                                                                className="text-red-600 hover:text-red-700 text-sm"
                                                                onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                                                            >
                                                                Remove
                                                            </button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>

                                            {uploadingAttachments && (
                                                <div className="mt-3 mb-3">
                                                    <div className="text-sm text-gray-600 mb-2">Uploading files...</div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                                                            style={{ width: `${uploadProgress}%` }}
                                                        ></div>
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-3 flex justify-end">
                                                <button
                                                    className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                                                    disabled={attachments.length === 0 || uploadingAttachments || processingFiles}
                                                    onClick={handleUploadAttachments}
                                                    title={processingFiles ? 'Please wait while files are being processed...' : ''}
                                                >
                                                    {uploadingAttachments ? 'Uploading...' : 'Upload'}
                                                </button>
                                                {processingFiles && (
                                                    <div className="ml-3 text-xs text-gray-500 flex items-center">
                                                        <svg className="animate-spin w-3 h-3 text-gray-400 mr-1" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Processing...
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {uploadSuccess && (
                                        <div className="mt-2 p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md">
                                            <div className="flex items-center">
                                                <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                </svg>
                                                <span className="font-medium">Attachments uploaded successfully!</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Action buttons - always inside details box */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            {actionError && (
                                <div className="text-sm text-red-600 mb-2">{actionError}</div>
                            )}
                            <div className="flex items-center justify-end gap-3">
                                <button
                                    onClick={() => handleAction('reject')}
                                    disabled={actionLoading}
                                    className={`px-4 py-2 rounded ${actionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white`}
                                >
                                    Reject Order
                                </button>
                                <button
                                    onClick={() => handleAction('finalize')}
                                    disabled={actionLoading}
                                    className={`px-4 py-2 rounded ${actionLoading ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'} text-white`}
                                >
                                    Finalize Order
                                </button>
                            </div>
                        </div>
                    </OrderDetailsView>
                </>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                            </div>
                        ) : error ? (
                            (
                                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-center">{error}</div>
                            )
                        ) : (orders.filter(order => {
                            const start = exportStartDate ? new Date(`${exportStartDate}T00:00:00`) : null;
                            const end = exportEndDate ? new Date(`${exportEndDate}T23:59:59`) : null;
                            if (order.status !== 'PENDING') return false;
                            const created = new Date(order.createdAt);
                            if (start && created < start) return false;
                            if (end && created > end) return false;
                            return true;
                        }).length === 0) ? (
                            <div className="p-8 text-center">
                                <p className="text-gray-500 text-lg">No orders found</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Order ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {(() => {
                                        const start = exportStartDate ? new Date(`${exportStartDate}T00:00:00`) : null;
                                        const end = exportEndDate ? new Date(`${exportEndDate}T23:59:59`) : null;
                                        const filteredOrders = orders.filter(order => {
                                            if (order.status !== 'PENDING') return false;
                                            const created = new Date(order.createdAt);
                                            if (start && created < start) return false;
                                            if (end && created > end) return false;
                                            return true;
                                        });
                                        return filteredOrders.map((order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {order.id}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {(() => {
                                                        const details = getDiscountDetails(order);
                                                        if (!details) return <span className="text-gray-400">N/A</span>;

                                                        const hasDiscount = details.discountByAmount > 0 || details.discountByPercentage > 0;

                                                        if (hasDiscount) {
                                                            return (
                                                                <div className="flex flex-col">
                                                                    <span className="text-xs text-gray-400 line-through">
                                                                        IQD {Number(details.paidPrice).toLocaleString()}
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-bold text-gray-900">
                                                                            IQD {Number(details.paidAmountAfterDiscount).toLocaleString()}
                                                                        </span>
                                                                        <span className="text-xs font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100 whitespace-nowrap">
                                                                            -{details.discountByPercentage > 0
                                                                                ? `${details.discountByPercentage}%`
                                                                                : `IQD ${Number(details.discountByAmount).toLocaleString()}`}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }

                                                        return `IQD ${Number(details.paidAmountAfterDiscount).toLocaleString()}`;
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDateTime(order.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button className="text-blue-600 hover:text-blue-900" onClick={() => handleViewDetails(order.id)}>
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SupplierPendingOrders;
